/**
 * Vendor risk score — implementation of the "GVM Recommended risk score model"
 * spec. Deterministic 0–100 score from business_details, VM enrichment, and
 * branch data. Category-specific questionnaire risk (component #5 in the spec)
 * is not yet implemented — pending schema-field alignment with BE.
 */

export type RiskLevel = "Low" | "Medium" | "High";

export type RiskComponent = {
  name: string;
  score: number;
  detail: string;
};

export type RiskResult = {
  totalScore: number;
  level: RiskLevel;
  components: RiskComponent[];
};

const SUPER_CATEGORY_RISK: Record<string, number> = {
  "Trade Raw Material - Wooden": 10,
  "Modular - Finished Goods": 10,
  "Raw Material - Hardware": 10,
  Furniture: 10,
  "Deep Cleaning": 10,
  "Floor Protection": 10,
  Decor: 10,
  Services: 20,
  "KWS Installation": 25,
  "Livpreneur Partner": 30,
  "Non-Trade - Marketing": 15,
  "Non-Trade - IT Services": 20,
  "Non-Trade - IT Materials": 20,
  "Non-Trade - Human Resources": 20,
  "Non-Trade - Facilities & Manpower": 25,
  "Retail Capex": 20,
};

function asString(v: unknown): string {
  if (v == null) return "";
  return String(v);
}

function isNo(v: unknown): boolean {
  if (v === false) return true;
  return asString(v).toLowerCase() === "no";
}

function hasAttachment(v: unknown): boolean {
  if (v && typeof v === "object") {
    return Boolean((v as { attachment_id?: string }).attachment_id);
  }
  return false;
}

function vendorTypeRisk(businessData: Record<string, unknown>): RiskComponent {
  const vt = asString(businessData.vendor_type);
  const score =
    vt === "GST" ? 0 : vt === "Non-GST" ? 10 : vt === "International" ? 20 : 0;
  return { name: "Vendor Type", score, detail: vt || "—" };
}

function commercialTermsRisk(vmData: Record<string, unknown>): RiskComponent {
  const pattern = asString(vmData.payment_terms_pattern);
  const basis = asString(vmData.first_tranche_payment_basis).toLowerCase();
  const creditDays = Number(vmData.credit_days ?? 0);

  let patternScore = 0;
  let patternDetail = "";
  if (pattern === "0-100") {
    patternScore = 0;
  } else if (pattern === "100-0") {
    patternScore = 25;
    patternDetail = "100-0 pattern (+25)";
  } else if (basis.includes("advance before milestone")) {
    patternScore = 15;
    patternDetail = "advance before milestone (+15)";
  } else if (pattern) {
    patternScore = 5;
    patternDetail = `split pattern ${pattern} (+5)`;
  }

  const creditScore =
    Number.isFinite(creditDays) && creditDays > 60 ? 10 : 0;
  const autoValScore = isNo(vmData.auto_invoice_validate) ? 10 : 0;

  const parts: string[] = [];
  if (patternDetail) parts.push(patternDetail);
  if (creditScore) parts.push(`credit ${creditDays}d > 60 (+10)`);
  if (autoValScore) parts.push("auto-validate No (+10)");

  return {
    name: "Commercial Terms",
    score: patternScore + creditScore + autoValScore,
    detail: parts.join(", ") || "No commercial-terms risk factors",
  };
}

function quoteRisk(vmData: Record<string, unknown>): RiskComponent {
  let score = 0;
  const parts: string[] = [];
  if (isNo(vmData.quotes_taken_over_email)) {
    score += 10;
    parts.push("quotes not over email (+10)");
  }
  if (isNo(vmData.three_vendor_quotes_available)) {
    score += 15;
    parts.push("< 3 vendor quotes (+15)");
  }
  if (isNo(vmData.l1_vendor_selected)) {
    score += 20;
    parts.push("non-L1 vendor (+20)");
  }
  return {
    name: "Quote / Selection",
    score,
    detail: parts.join(", ") || "All quote controls met",
  };
}

function superCategoryRisk(vmData: Record<string, unknown>): RiskComponent {
  const cat = asString(vmData.super_category);
  const score = cat ? SUPER_CATEGORY_RISK[cat] ?? 10 : 0;
  return { name: "Super Category", score, detail: cat || "—" };
}

function documentRisk(
  businessData: Record<string, unknown>,
  branches: Record<string, unknown>[],
): RiskComponent {
  let score = 0;
  const parts: string[] = [];

  const panMissing =
    !hasAttachment(businessData.pan_card_attachment) &&
    !hasAttachment(businessData.pan_card);
  if (panMissing) {
    score += 25;
    parts.push("PAN card (+25)");
  }

  // Branch-level document checks — if no branches resolved (empty submission
  // or upstream fetch not yet included), we cannot assert per-branch
  // completeness either way. [].some(...) === false would otherwise mis-read
  // as "everything present" and let the reviewer see a false all-clear.
  if (branches.length === 0) {
    return {
      name: "Documents",
      score,
      detail: parts.length
        ? `Missing (branch docs not resolved): ${parts.join(", ")}`
        : "Branch documents not resolved — cannot assert completeness",
    };
  }

  const vt = asString(businessData.vendor_type);
  if (vt === "GST") {
    const anyGstMissing = branches.some(
      (b) => !hasAttachment(b.facility_gst_certificate_attachment),
    );
    if (anyGstMissing) {
      score += 25;
      parts.push("GST certificate (+25)");
    }
  }

  const anyBankMissing = branches.some(
    (b) => !hasAttachment(b.cancelled_cheque_attachment),
  );
  if (anyBankMissing) {
    score += 25;
    parts.push("Bank proof (+25)");
  }

  return {
    name: "Documents",
    score,
    detail: parts.length
      ? `Missing: ${parts.join(", ")}`
      : "All mandatory documents uploaded",
  };
}

export function computeVendorRiskScore(
  businessData: Record<string, unknown> | undefined,
  vmData: Record<string, unknown> | undefined,
  branches: Record<string, unknown>[] | undefined,
): RiskResult {
  const bd = businessData ?? {};
  const vm = vmData ?? {};
  const br = branches ?? [];
  const components: RiskComponent[] = [
    vendorTypeRisk(bd),
    commercialTermsRisk(vm),
    quoteRisk(vm),
    superCategoryRisk(vm),
    documentRisk(bd, br),
  ];
  const raw = components.reduce((sum, c) => sum + c.score, 0);
  const totalScore = Math.min(raw, 100);
  const level: RiskLevel =
    totalScore <= 30 ? "Low" : totalScore <= 60 ? "Medium" : "High";
  return { totalScore, level, components };
}
